import { forwardRef, Ref, RefAttributes, type JSX, useCallback, useEffect, useRef, useId } from "react";

import { CheckboxProps as MuiCheckboxProps } from "@mui/material/Checkbox";
import { useTheme } from "@mui/material/styles";
import classnames from "classnames";
import { FieldValues, Path, PathValue, useController, useWatch } from "react-hook-form";

import { useIsFormV2 } from "../../../features/FormV2/hooks/useIsFormV2";
import { getLabel, isInputRequired } from "../../../helpers/validators";
import { useCurrentInputVibe } from "../../../hooks/useCurrentInputVibe";
import { useCustomInputProps } from "../../../hooks/useCustomInputProps";
import { DashboardIcon } from "../Icon";
import { DashboardOptionLabel } from "../OptionLabel";
import { useMergeClasses } from "../useMergeClasses";

import { zeffyCheckboxClasses, StyledCheckbox } from "./styles";
import { CheckboxProps, BaseCheckboxProps, FormCheckboxProps } from "./types";

import { useCustomError } from "@/hooks/useCustomError";
import { useFormInputRefs } from "@/hooks/useFormInputRefs";
import { useValidators } from "@/hooks/useValidators";
import { Check, Minus } from "@/icons/outlined";

function CheckboxInner<T extends FieldValues>(
  { control, name, ...rest }: CheckboxProps<T>,
  ref: Ref<HTMLButtonElement>
) {
  return control && name ? (
    <FormCheckbox control={control} name={name} {...rest} ref={ref} />
  ) : (
    <BaseCheckbox {...rest} name={name} ref={ref} />
  );
}

CheckboxInner.displayName = "Checkbox";

export const DashboardCheckbox = forwardRef(CheckboxInner) as <T extends FieldValues>(
  props: CheckboxProps<T> & RefAttributes<HTMLButtonElement>
) => JSX.Element;

function FormCheckboxInner<T extends FieldValues>(
  {
    blockFormOnChange,
    control,
    defaultChecked,
    name,
    onBlur,
    onChange,
    rules,
    noAsterisk,
    ...rest
  }: FormCheckboxProps<T>,
  ref: Ref<HTMLButtonElement>
) {
  const elementRef = useRef<HTMLElement | null>(null);
  const validators = useValidators(rules);
  const isRequired = isInputRequired(validators);
  const watchedValue = useWatch({ control, name });
  const currentDefaultChecked = watchedValue ?? (defaultChecked as PathValue<T, Path<T>>);

  const { field } = useController<T>({
    name,
    control,
    defaultValue: currentDefaultChecked,
    rules: validators,
  });

  const handleChange = useCallback<NonNullable<BaseCheckboxProps["onChange"]>>(
    (event, value) => {
      if (!blockFormOnChange) {
        field.onChange(value);
      }
      onChange?.(event, value);
    },
    [blockFormOnChange, field, onChange]
  );

  const handleBlur = useCallback<NonNullable<BaseCheckboxProps["onBlur"]>>(
    (event) => {
      field.onBlur();
      onBlur?.(event);
    },
    [field, onBlur]
  );

  const refCallBack = useFormInputRefs({
    inputRefCallBack: (element: HTMLInputElement | null) => {
      field.ref(element);
      elementRef.current = element;
    },
  });

  /* Workaround to scroll checkbox element into view when focused on iOS */
  useEffect(() => {
    const element = elementRef?.current;

    if (!element) {
      return;
    }

    let wasClicked = false;

    const handlePress = () => {
      wasClicked = true;
    };

    const handleFocus = () => {
      if (!wasClicked) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      // Reset the flag after handling focus
      wasClicked = false;
    };

    element.addEventListener("mousedown", handlePress);
    element.addEventListener("touchstart", handlePress);
    element.addEventListener("focus", handleFocus);

    return () => {
      element.removeEventListener("mousedown", handlePress);
      element.removeEventListener("touchstart", handlePress);
      element.removeEventListener("focus", handleFocus);
    };
  }, []);

  return (
    <BaseCheckbox
      onBlur={handleBlur}
      onChange={handleChange}
      {...rest}
      ref={ref}
      inputRef={refCallBack}
      checked={Boolean(field.value)}
      value={field.value}
      label={getLabel(isRequired, rest.label, noAsterisk)}
      name={name}
    />
  );
}

FormCheckboxInner.displayName = "FormCheckbox";

const FormCheckbox = forwardRef(FormCheckboxInner) as unknown as <T extends FieldValues>(
  props: FormCheckboxProps<T> & RefAttributes<HTMLButtonElement>
) => JSX.Element;

const BaseCheckbox = forwardRef<HTMLButtonElement, BaseCheckboxProps>(
  (
    {
      alignContent,
      className,
      classes: externalClasses,
      disabled = false,
      vibe,
      error,
      errorMessage,
      fullWidth,
      helperText,
      indeterminate = false,
      inputProps,
      inputRef,
      label,
      onChange,
      optionLabelClasses = {},
      size = "medium",
      tooltipText,
      "data-test": dataTest,
      name,
      style,
      clickableLabel: clickableLabelProps,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const currentInputVibe = useCurrentInputVibe(vibe);
    const classes = useMergeClasses(zeffyCheckboxClasses, externalClasses);
    const isFormV2 = useIsFormV2();

    const handleChange = useCallback<NonNullable<MuiCheckboxProps["onChange"]>>(
      (event, checked) => {
        onChange?.(event, checked);
      },
      [onChange]
    );

    const currentError = useCustomError(error, errorMessage);

    const clickableLabel = (clickableLabelProps ?? isFormV2) && !disabled;
    const inputId = useId();
    const realInputId = clickableLabel ? name || inputId : undefined;

    const customInputProps = useCustomInputProps({
      inputProps: { ...inputProps, id: realInputId },
      dataTest: "checkbox-input",
    });

    return (
      <DashboardOptionLabel
        alignContent={alignContent}
        className={className}
        classes={optionLabelClasses}
        vibe={currentInputVibe}
        data-test={dataTest}
        disabled={disabled}
        errorMessage={errorMessage}
        fullWidth={fullWidth}
        helperText={helperText}
        label={label}
        size={size}
        tooltipText={tooltipText}
        style={style}
        labelSx={{
          cursor: clickableLabel ? "pointer" : "default",
        }}
        inputId={realInputId}
      >
        <StyledCheckbox
          id={realInputId}
          classes={{
            root: classnames(classes.root, {
              [classes.small]: size === "small",
              [classes.formV2]: theme.constants.isFormV2,
              [classes.error]: error,
            }),
            disabled: classes.disabled,
          }}
          color="primary"
          checkedIcon={
            <div
              className={classnames(classes.icon, classes.filledIcon, {
                [classes.small]: size === "small",
              })}
            >
              <DashboardIcon vibe="inherit" size={size}>
                <Check />
              </DashboardIcon>
            </div>
          }
          disabled={disabled}
          icon={
            <div
              className={classnames(classes.icon, classes.emptyIcon, {
                [classes.iconForm]: currentInputVibe === "form",
                [classes.small]: size === "small",
                [classes.error]: currentError,
              })}
            />
          }
          indeterminate={indeterminate}
          indeterminateIcon={
            <div
              className={classnames(classes.icon, classes.filledIcon, {
                [classes.small]: size === "small",
              })}
            >
              <DashboardIcon vibe="inherit" size={size}>
                <Minus />
              </DashboardIcon>
            </div>
          }
          inputProps={customInputProps}
          inputRef={inputRef}
          onChange={handleChange}
          ref={ref}
          size={size}
          disableRipple
          name={name}
          {...rest}
        />
      </DashboardOptionLabel>
    );
  }
);

BaseCheckbox.displayName = "Checkbox";
